import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  SERVICE_CANCELLATION_SEARCH_FOTOTREE,
  SERVICE_CANCELLED_RESPONSE,
} from 'consts'
import {useTranslation} from 'i18n'
import {requestDataPayload} from 'services'
import {TreeSearchFototreeResponse, GiftShopUploadType} from 'types'
import {
  getGiftshopLastUploadFototree,
  getGiftshopLocationName,
  useDebounce,
  useDidMount,
  useDidUpdate,
} from 'utils'
import {Icon, Input, ListLazy, Menu, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopTemplateUploadFormTagsProps} from './GiftShopTemplateUploadFormTagsProps'
import {GiftshopTemplateUploadFormTagsTagItem} from './GiftshopTemplateUploadFormTagsTagItem'

interface StyledTagsProps {
  uploadType?: GiftShopUploadType
}

const StyledContentListContainer = styled.div`
  border-top: ${convertUnit(1)};
  border-style: solid;
  border-color: ${({theme}) => theme.gray_1};
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  flex: 1;
`

const StyledTags = styled.div<StyledTagsProps>`
  width: ${({uploadType}) => (uploadType === 'bulk' ? '100%' : 'unset')};
`

const StyledEmptyContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  word-break: break-word;
  padding: ${convertUnit(8)} ${convertUnit(16)};
  text-align: center;
`

const StyledBold = styled.span`
  font-family: 'Roboto-Bold';
`

const StyledParagraph = styled(Paragraph)`
  margin: ${convertUnit(10)} ${convertUnit(0)};
`

export default function GiftShopTemplateUploadFormTags({
  form,
  tags,
  uploadType,
  hasLocationMetadata = false,
  stateSearchTag,
  stateSearchQuery,
  handleValidateTagsAndLocation,
  handleClearTagsAndLocation,
}: GiftShopTemplateUploadFormTagsProps) {
  const {translate} = useTranslation()
  const debounce = useDebounce()
  const [
    lastUploadTag,
    setLastUploadTag,
  ] = useState<TreeSearchFototreeResponse>()
  const stateTags = useState<readonly TreeSearchFototreeResponse[]>([])
  const [contentTags, setTags] = stateTags
  const [searchTag, setSearchTag] = stateSearchTag
  const [searchQuery, setSearchQuery] = stateSearchQuery
  const stateToggle = useState(false)
  const setToggle = stateToggle[1]
  const falseState = useState(false)
  const {setValue, watch} = form
  const values = watch()
  const selectedTags = values.tags
  const {location} = values
  const [error, setError] = useState<{
    isError: boolean
    detail?: string
  }>({
    isError: false,
  })
  const disabled = location !== '' && selectedTags.length === 0

  const handleLoadCachedTag = useCallback(() => {
    const cachedTag = getGiftshopLastUploadFototree({
      uploadType: 'bulk',
    })
    if (!cachedTag) return

    setLastUploadTag(cachedTag)
  }, [])

  const lastCachedContentTag = useMemo(
    () =>
      contentTags.find((tag) => tag.tag_id === lastUploadTag?.tag_id) ??
      lastUploadTag,
    [contentTags, lastUploadTag],
  )

  const handleLoadData = useCallback(
    async (page: number, limit: number, q: string) => {
      const response = await requestDataPayload('tree_search_fototree', {
        useDefaultMessage: true,
        actionType: 'fetch',
        cancelId: SERVICE_CANCELLATION_SEARCH_FOTOTREE,
        params: {page, limit, name: q, is_upload: true},
      })

      if (
        typeof response === 'string' &&
        response === SERVICE_CANCELLED_RESPONSE
      ) {
        return undefined
      }

      return typeof response !== 'string' && response?.status === 200
        ? response.data.result.filter(
            (item) =>
              selectedTags.findIndex((tag) => tag.tag_id === item.tag_id) ===
              -1,
          )
        : null
    },
    [selectedTags],
  )

  const handleSelect = useCallback(
    (item: TreeSearchFototreeResponse) => {
      setError({isError: false})
      setSearchTag(item.name)
      if (selectedTags.filter((value) => value.id === item.id).length) {
        setToggle((previous) => !previous)
        return
      }
      if (
        !values.location ||
        Object.keys(values.location).length === 0 ||
        !hasLocationMetadata
      ) {
        const {location: tagLocation, name} = item
        const locationVal = getGiftshopLocationName({
          latitude: tagLocation?.latitude.toString(),
          longitude: tagLocation?.longitude.toString(),
          name,
        })
        setValue('location', locationVal)
      }
      setValue('tags', [...values.tags, item])
      setToggle((previous) => !previous)
      setTags((previous) =>
        previous.filter((value) => value.tag_id !== item.tag_id),
      )
    },
    [
      hasLocationMetadata,
      selectedTags,
      setSearchTag,
      setTags,
      setToggle,
      setValue,
      values.location,
      values.tags,
    ],
  )

  const handleRenderPinnedDropdownItem = useMemo(
    () =>
      contentTags.length > 0 && lastCachedContentTag ? (
        <GiftshopTemplateUploadFormTagsTagItem
          item={lastCachedContentTag}
          selectedTags={selectedTags}
          handleSelect={handleSelect}
          type="last-used"
        />
      ) : (
        <></>
      ),
    [contentTags.length, handleSelect, lastCachedContentTag, selectedTags],
  )

  const handleRenderFilteredDropdownItem = useCallback(
    (item: TreeSearchFototreeResponse) =>
      lastUploadTag?.tag_id === item.tag_id ? (
        <></>
      ) : (
        <GiftshopTemplateUploadFormTagsTagItem
          item={item}
          selectedTags={selectedTags}
          handleSelect={handleSelect}
          type="search-result"
        />
      ),
    [handleSelect, lastUploadTag?.tag_id, selectedTags],
  )

  const handleRenderTagListEmpty = useMemo(
    () => (
      <StyledEmptyContainer>
        <Paragraph fontSize="m">
          {translate('giftShop:uploadInputTagsNotFound')}
          <StyledBold>{`"${searchTag}"`}</StyledBold>
        </Paragraph>
      </StyledEmptyContainer>
    ),
    [searchTag, translate],
  )

  const handleRenderTagList = useMemo(
    () => (
      <StyledContentListContainer>
        <ListLazy
          stateData={stateTags}
          loadData={handleLoadData}
          search={searchQuery}
          data={contentTags}
          keyExtractor={(tag) => tag.tag_id}
          renderItem={handleRenderFilteredDropdownItem}
          listEmptyElement={handleRenderTagListEmpty}
          listHeaderElement={handleRenderPinnedDropdownItem}
        />
      </StyledContentListContainer>
    ),
    [
      contentTags,
      handleLoadData,
      handleRenderFilteredDropdownItem,
      handleRenderPinnedDropdownItem,
      handleRenderTagListEmpty,
      searchQuery,
      stateTags,
    ],
  )

  const handleUpdateErrorTagsAndLocation = useCallback(() => {
    const isBlank = !handleValidateTagsAndLocation({})

    setError({
      isError: isBlank || !!(searchTag && !selectedTags.length),
      detail:
        searchTag && !selectedTags.length
          ? translate('giftShop:uploadInputTagsNotSelected')
          : undefined,
    })
  }, [handleValidateTagsAndLocation, searchTag, selectedTags.length, translate])

  useDidUpdate(() => {
    handleUpdateErrorTagsAndLocation()
  }, [handleUpdateErrorTagsAndLocation, location])

  useDidMount(() => {
    handleLoadCachedTag()
  })

  return (
    <StyledTags uploadType={uploadType}>
      <Menu
        stateToggle={
          selectedTags.filter((tag) => tag.type !== 'host').length < 1
            ? stateToggle
            : falseState
        }
        contentContainerStyle={{
          height: convertUnit(250),
          borderRadius: convertUnit(8),
        }}
        renderContent={handleRenderTagList}>
        <Input
          value={searchTag}
          leftIcon={
            <Icon
              type="tree-feature"
              color={disabled || tags.length === 0 ? 'gray_3' : 'black_1'}
            />
          }
          rightIcon="delete"
          onRightIconClick={handleClearTagsAndLocation}
          placeholder={translate('giftShop:uploadInputTagsPlaceholder')}
          borderColor={error.isError ? 'danger_5' : undefined}
          hoverBorderColor={error.isError ? 'danger_5' : undefined}
          onChangeText={(text) => {
            setSearchTag(text)
            debounce(() => setSearchQuery(text), 500)
            setError({isError: false})
            if (!text) handleClearTagsAndLocation()
          }}
          onBlur={handleUpdateErrorTagsAndLocation}
          onFocus={() => {
            setError({isError: false})
            setToggle(true)
          }}
          disabled={disabled}
        />
      </Menu>
      {error.isError && error.detail ? (
        <StyledParagraph color="danger_5" fontWeight="medium">
          {error.detail}
        </StyledParagraph>
      ) : (
        <></>
      )}
    </StyledTags>
  )
}
